import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { subscriptionService } from '../lib/supabase';
import { PlanId, PlanConfig, PLAN_CONFIGS, DEFAULT_PLAN_ID } from '../config/plans';
import { WeddingInvitation } from '../types/invitation';
import { createLogger } from '../lib/logger';

const log = createLogger('subscription');

interface SubscriptionContextType {
  planId: PlanId;
  limits: PlanConfig;
  isLoading: boolean;
  canAddGuest: (invitation: WeddingInvitation | undefined, currentAttendingCount: number) => boolean;
  canAddTable: (invitation: WeddingInvitation | undefined, currentTableCount: number) => boolean;
  canExport: (invitation: WeddingInvitation | undefined) => boolean;
  canCollaborate: () => boolean;
  canCustomizeBranding: () => boolean;
  canUseAITimeline: () => boolean;
  canCreateInvitation: (currentActiveCount: number) => boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

const isCollaborator = (invitation: WeddingInvitation | undefined): boolean => {
  return invitation?.role === 'collaborator';
};

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [planId, setPlanId] = useState<PlanId>(DEFAULT_PLAN_ID);
  const [isLoading, setIsLoading] = useState(true);

  const limits = PLAN_CONFIGS[planId];

  const loadPlan = useCallback(async () => {
    if (!user) {
      setPlanId(DEFAULT_PLAN_ID);
      setIsLoading(false);
      return;
    }

    try {
      const effectivePlan = await subscriptionService.getEffectivePlanId(user.id);
      setPlanId(effectivePlan);
    } catch (error) {
      log.error('Failed to load subscription:', error);
      setPlanId(DEFAULT_PLAN_ID);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const canAddGuest = useCallback((invitation: WeddingInvitation | undefined, currentAttendingCount: number): boolean => {
    if (isCollaborator(invitation)) return true;
    if (limits.maxGuests === null) return true;
    return currentAttendingCount < limits.maxGuests;
  }, [limits]);

  const canAddTable = useCallback((invitation: WeddingInvitation | undefined, currentTableCount: number): boolean => {
    if (isCollaborator(invitation)) return true;
    if (limits.maxTables === null) return true;
    return currentTableCount < limits.maxTables;
  }, [limits]);

  const canExport = useCallback((invitation: WeddingInvitation | undefined): boolean => {
    if (isCollaborator(invitation)) return true;
    return limits.canExport;
  }, [limits]);

  const canCollaborate = useCallback((): boolean => {
    return limits.canCollaborate;
  }, [limits]);

  const canCustomizeBranding = useCallback((): boolean => {
    return limits.canCustomizeBranding;
  }, [limits]);

  const canUseAITimeline = useCallback((): boolean => {
    return limits.canUseAITimeline;
  }, [limits]);

  const canCreateInvitation = useCallback((currentActiveCount: number): boolean => {
    return currentActiveCount < limits.maxActiveInvitations;
  }, [limits]);

  const value: SubscriptionContextType = {
    planId,
    limits,
    isLoading,
    canAddGuest,
    canAddTable,
    canExport,
    canCollaborate,
    canCustomizeBranding,
    canUseAITimeline,
    canCreateInvitation,
    refresh: loadPlan,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
